/**
 * [TM-01..TM-03] โมดูลงานภายใน (internal tasks) — สัญญาข้าม worker↔web ที่เดียว · tasks/TM-CONTRACTS.md
 * ขอบเขต MVP: โปรเจกต์ + บอร์ด Kanban/List + งาน/งานย่อย/เช็กลิสต์/ป้ายกำกับ/ผู้ติดตาม/คอมเมนต์/ประวัติ
 * สิทธิ์: requireStaff ทุก route · visibility company = พนักงานทุกคนอ่านได้ (เขียนต้องเป็นสมาชิก)
 *        private = เฉพาะสมาชิก (non-member เห็น 404 ไม่ใช่ 403 — ตามกฎ "ไม่ยืนยันว่ามีอยู่")
 * notifications/email/task↔ticket = wave ถัดไป (TM-04/TM-05) — ยังไม่อยู่ในสัญญานี้
 */
import { z } from "zod";

export const TaskVisibility = z.enum(["company", "private"]);
export type TaskVisibility = z.infer<typeof TaskVisibility>;

export const TaskPriority = z.enum(["low", "normal", "high", "urgent"]);
export type TaskPriority = z.infer<typeof TaskPriority>;

export const TaskStatusKind = z.enum(["open", "in_progress", "done"]);
export type TaskStatusKind = z.infer<typeof TaskStatusKind>;

export const TaskProjectRole = z.enum(["manager", "member"]);
export type TaskProjectRole = z.infer<typeof TaskProjectRole>;

/** user ref ที่ serializer ส่งข้ามฝั่ง — น้อยที่สุด (PII minimization) */
export const TaskUserRef = z.object({
  user_id: z.string().uuid(),
  name: z.string(),
});
export type TaskUserRef = z.infer<typeof TaskUserRef>;

// ---------- project ----------

export const TaskProjectCreate = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(2000).default(""),
  icon: z.string().max(16).optional(),
  color: z.string().max(16).optional(),
  visibility: TaskVisibility.default("company"),
});
export type TaskProjectCreate = z.infer<typeof TaskProjectCreate>;

export const TaskProjectPatch = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().max(2000).optional(),
  icon: z.string().max(16).nullable().optional(),
  color: z.string().max(16).nullable().optional(),
  visibility: TaskVisibility.optional(),
});
export type TaskProjectPatch = z.infer<typeof TaskProjectPatch>;

/** คอลัมน์ของบอร์ด */
export const TaskStatus = z.object({
  id: z.string().uuid(),
  project_id: z.string().uuid(),
  name: z.string(),
  kind: TaskStatusKind,
  position: z.number().int().nonnegative(),
});
export type TaskStatus = z.infer<typeof TaskStatus>;

export const TaskLabel = z.object({
  id: z.string().uuid(),
  project_id: z.string().uuid(),
  name: z.string(),
  color: z.string(),
});
export type TaskLabel = z.infer<typeof TaskLabel>;

export const TaskProjectCounts = z.object({
  open: z.number().int().nonnegative(),
  done: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
});
export type TaskProjectCounts = z.infer<typeof TaskProjectCounts>;

export const TaskProjectSummary = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  visibility: TaskVisibility,
  /** null = ผู้เรียกไม่ใช่สมาชิก (โปรเจกต์ company ที่ดูอย่างเดียว) */
  my_role: TaskProjectRole.nullable(),
  task_counts: TaskProjectCounts,
  archived_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
});
export type TaskProjectSummary = z.infer<typeof TaskProjectSummary>;

export const TaskProjectDetail = TaskProjectSummary.extend({
  statuses: z.array(TaskStatus),
  labels: z.array(TaskLabel),
});
export type TaskProjectDetail = z.infer<typeof TaskProjectDetail>;

export const TaskProjectList = z.object({
  items: z.array(TaskProjectSummary),
});
export type TaskProjectList = z.infer<typeof TaskProjectList>;

export const TaskMember = z.object({
  user: TaskUserRef,
  role: TaskProjectRole,
  added_at: z.iso.datetime(),
});
export type TaskMember = z.infer<typeof TaskMember>;

/** เชิญรายคน (user_ids) หรือทั้งทีม (team_id — server ขยายเป็นสมาชิก ณ เวลาเชิญ) */
export const TaskMemberAdd = z
  .object({
    user_ids: z.array(z.string().uuid()).min(1).max(50).optional(),
    team_id: z.string().uuid().optional(),
    role: TaskProjectRole.default("member"),
  })
  .refine((v) => v.user_ids !== undefined || v.team_id !== undefined, { message: "user_ids or team_id required" });
export type TaskMemberAdd = z.infer<typeof TaskMemberAdd>;

// ---------- status / label (manager only) ----------

export const TaskStatusCreate = z.object({
  name: z.string().trim().min(1).max(60),
  kind: TaskStatusKind.default("open"),
  position: z.number().int().nonnegative().optional(),
});
export type TaskStatusCreate = z.infer<typeof TaskStatusCreate>;

export const TaskStatusPatch = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  kind: TaskStatusKind.optional(),
  position: z.number().int().nonnegative().optional(),
});
export type TaskStatusPatch = z.infer<typeof TaskStatusPatch>;

export const TaskLabelCreate = z.object({
  name: z.string().trim().min(1).max(40),
  color: z.string().max(16).default("#97a4b0"),
});
export type TaskLabelCreate = z.infer<typeof TaskLabelCreate>;

export const TaskLabelPatch = z.object({
  name: z.string().trim().min(1).max(40).optional(),
  color: z.string().max(16).optional(),
});
export type TaskLabelPatch = z.infer<typeof TaskLabelPatch>;

// ---------- task ----------

/** การ์ดบนบอร์ด/รายการ — เพรียว ๆ พอสำหรับ render คอลัมน์ */
export const TaskCard = z.object({
  id: z.string().uuid(),
  project_id: z.string().uuid(),
  parent_task_id: z.string().uuid().nullable(),
  title: z.string(),
  status_id: z.string().uuid(),
  status_kind: TaskStatusKind,
  position: z.number().int().nonnegative(),
  assignee: TaskUserRef.nullable(),
  priority: TaskPriority,
  due_at: z.iso.datetime().nullable(),
  labels: z.array(TaskLabel),
  checklist_done: z.number().int().nonnegative(),
  checklist_total: z.number().int().nonnegative(),
  comment_count: z.number().int().nonnegative(),
  subtask_done: z.number().int().nonnegative(),
  subtask_total: z.number().int().nonnegative(),
  /** server คำนวณ: due_at อดีตกว่า now และ status_kind != done */
  overdue: z.boolean(),
  expected_version: z.number().int().min(1),
});
export type TaskCard = z.infer<typeof TaskCard>;

export const TaskBoard = z.object({
  statuses: z.array(TaskStatus),
  labels: z.array(TaskLabel),
  columns: z.array(z.object({ status_id: z.string().uuid(), tasks: z.array(TaskCard) })),
});
export type TaskBoard = z.infer<typeof TaskBoard>;

export const TaskList = z.object({
  items: z.array(TaskCard),
  total: z.number().int().nonnegative(),
});
export type TaskList = z.infer<typeof TaskList>;

/** ?q&status_id&assignee_user_id&label_id&priority&overdue&parent_task_id&limit&offset */
export const TaskListQuery = z.object({
  q: z.string().max(200).optional(),
  status_id: z.string().uuid().optional(),
  assignee_user_id: z.string().uuid().optional(),
  label_id: z.string().uuid().optional(),
  priority: TaskPriority.optional(),
  overdue: z.enum(["true"]).optional(),
  parent_task_id: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).max(10_000).default(0),
});
export type TaskListQuery = z.infer<typeof TaskListQuery>;

export const TaskCreate = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().max(50_000).default(""),
  status_id: z.string().uuid().optional(),
  assignee_user_id: z.string().uuid().optional(),
  priority: TaskPriority.default("normal"),
  due_at: z.iso.datetime().nullable().optional(),
  parent_task_id: z.string().uuid().optional(),
  label_ids: z.array(z.string().uuid()).max(20).default([]),
});
export type TaskCreate = z.infer<typeof TaskCreate>;

/** expected_version บังคับ — conditional UPDATE ตาม CONVENTIONS (0 แถว = 409 version_conflict) */
export const TaskPatch = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(50_000).optional(),
  assignee_user_id: z.string().uuid().nullable().optional(),
  priority: TaskPriority.optional(),
  due_at: z.iso.datetime().nullable().optional(),
  status_id: z.string().uuid().optional(),
  position: z.number().int().nonnegative().optional(),
  label_ids: z.array(z.string().uuid()).max(20).optional(),
  expected_version: z.number().int().min(1),
});
export type TaskPatch = z.infer<typeof TaskPatch>;

// ---------- [TM-04] จุดเชื่อม ticket (task → ticket ทางเดียว ตาม feature doc §4.6 รอบแรก) ----------

/** การ์ดลูกค้าที่เชื่อมจากงาน */
export const TaskTicketRef = z.object({
  ticket_id: z.string().uuid(),
  public_id: z.string(),
  subject: z.string(),
  status_type: z.enum(["open", "pending_customer", "pending_internal", "resolved", "closed"]),
  linked_by: TaskUserRef.nullable(),
  created_at: z.iso.datetime(),
});
export type TaskTicketRef = z.infer<typeof TaskTicketRef>;

export const TaskTicketLinkList = z.object({ items: z.array(TaskTicketRef) });
export type TaskTicketLinkList = z.infer<typeof TaskTicketLinkList>;

/** สร้างการ์ดจากงาน — subject ไม่ส่ง = ใช้หัวข้องาน · body_html ไม่ส่ง = ระบบรวมจาก description + ลิงก์กลับงาน */
export const TaskTicketLinkCreate = z.object({
  service_code: z.string().regex(/^[A-Z0-9]{2,4}$/),
  subject: z.string().trim().min(1).max(998).optional(),
  body_html: z.string().max(200_000).optional(),
  team_code: z.string().max(16).optional(),
});
export type TaskTicketLinkCreate = z.infer<typeof TaskTicketLinkCreate>;

export const TaskDetail = z.object({
  task: TaskCard,
  title: z.string(),
  description: z.string(),
  created_by: TaskUserRef.nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  completed_at: z.iso.datetime().nullable(),
  project: z.object({ id: z.string().uuid(), name: z.string(), visibility: TaskVisibility }),
  permissions: z.object({ can_edit: z.boolean(), can_manage: z.boolean() }),
  subtasks: z.array(TaskCard),
  checklist: z.array(
    z.object({
      id: z.string().uuid(),
      body: z.string(),
      position: z.number().int().nonnegative(),
      done: z.boolean(),
      done_at: z.iso.datetime().nullable(),
    }),
  ),
  watchers: z.array(TaskUserRef),
  /** [TM-04] การ์ดลูกค้าที่เชื่อมจากงานนี้ (task → ticket ทางเดียว) */
  tickets: z.array(TaskTicketRef),
});
export type TaskDetail = z.infer<typeof TaskDetail>;

// ---------- comment / checklist / watcher / event ----------

export const TaskCommentCreate = z.object({
  body: z.string().trim().min(1).max(10_000),
});
export type TaskCommentCreate = z.infer<typeof TaskCommentCreate>;

export const TaskComment = z.object({
  id: z.string().uuid(),
  task_id: z.string().uuid(),
  user: TaskUserRef,
  body: z.string(),
  /** server แกะ @ชื่อ จากสมาชิกโปรเจกต์ (เตรียมให้ TM-04 notifications — ยังไม่แจ้งเตือนใน wave นี้) */
  mentions: z.array(TaskUserRef),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type TaskComment = z.infer<typeof TaskComment>;

export const TaskCommentList = z.object({ items: z.array(TaskComment) });
export type TaskCommentList = z.infer<typeof TaskCommentList>;

export const TaskChecklistItemCreate = z.object({
  body: z.string().trim().min(1).max(500),
});
export type TaskChecklistItemCreate = z.infer<typeof TaskChecklistItemCreate>;

export const TaskChecklistItemPatch = z.object({
  body: z.string().trim().min(1).max(500).optional(),
  done: z.boolean().optional(),
});
export type TaskChecklistItemPatch = z.infer<typeof TaskChecklistItemPatch>;

export const TaskWatcherAdd = z.object({
  /** ไม่ส่ง = ตัวเอง */
  user_id: z.string().uuid().optional(),
});
export type TaskWatcherAdd = z.infer<typeof TaskWatcherAdd>;

export const TaskEvent = z.object({
  id: z.string().uuid(),
  task_id: z.string().uuid(),
  actor: TaskUserRef.nullable(),
  type: z.string(),
  payload: z.record(z.string(), z.unknown()),
  created_at: z.iso.datetime(),
});
export type TaskEvent = z.infer<typeof TaskEvent>;

export const TaskEventList = z.object({ items: z.array(TaskEvent) });
export type TaskEventList = z.infer<typeof TaskEventList>;

// ---------- my work ----------

export const TaskScope = z.enum(["assigned", "watching", "created", "overdue"]);
export type TaskScope = z.infer<typeof TaskScope>;

export const TaskMyResponse = z.object({
  assigned: z.array(TaskCard),
  watching: z.array(TaskCard),
  created: z.array(TaskCard),
  overdue_count: z.number().int().nonnegative(),
});
export type TaskMyResponse = z.infer<typeof TaskMyResponse>;

// ---------- [TM-04] รายงานขั้นต่ำต่อโปรเจกต์ (feature doc §4.8) — มุมมอง Asia/Bangkok ----------

export const TaskWorkloadRow = z.object({
  user: TaskUserRef,
  open_count: z.number().int().nonnegative(),
  overdue_count: z.number().int().nonnegative(),
});
export type TaskWorkloadRow = z.infer<typeof TaskWorkloadRow>;

export const TaskDoneWeekRow = z.object({
  /** วันจันทร์ของสัปดาห์ รูปแบบ YYYY-MM-DD เขต Asia/Bangkok */
  week_start: z.string(),
  done_count: z.number().int().nonnegative(),
});
export type TaskDoneWeekRow = z.infer<typeof TaskDoneWeekRow>;

export const TaskReport = z.object({
  project_id: z.string().uuid(),
  workload: z.array(TaskWorkloadRow),
  overdue: z.array(TaskCard),
  done_per_week: z.array(TaskDoneWeekRow),
});
export type TaskReport = z.infer<typeof TaskReport>;
